/**
 * ProfessionalsPage
 * Lists professionals with filtering, sorting, and pagination
 */

import { useAuth } from '@/shared/contexts'
import { useProfessionalsByProfession } from '@features/professionals'
import { ProfessionalsGrid } from '@features/professionals/components/ProfessionalsGrid'
import { ProfessionalsListHeader } from '@features/professionals/components/ProfessionalsListHeader'
import { ProfessionalsPagination } from '@features/professionals/components/ProfessionalsPagination'
import { ProfessionalsSortingFilter } from '@features/professionals/components/ProfessionalsSortingFilter'
import { useProfessions } from '@features/professions/hooks'
import { useProfessionalsListTour } from '@shared/hooks'
import type { Professional, SortOption } from '@shared/types'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

// ============================================================================
// Component
// ============================================================================

export function ProfessionalsPage() {
  const { professionId } = useParams<{ professionId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user } = useAuth()

  // Auto-start tour for first-time visitors
  useProfessionalsListTour(true)

  // Scroll to top when component loads or professionId changes
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [professionId, searchParams])

  // State
  // Use ONLY user.cityId (the ObjectId). Never fall back to user.city, which is
  // a city NAME string and would be sent as a bad `cityId` query param (backend 500s).
  const [cityId, setCityId] = useState<string>(user?.cityId || '')
  const [gender, setGender] = useState<string>('all')
  const [minRating, setMinRating] = useState<string>('all')
  const [sortBy, setSortBy] = useState<SortOption>('rating-desc')

  // Update cityId when user changes (e.g., after login)
  useEffect(() => {
    if (user?.cityId) {
      setCityId(user.cityId)
    }
  }, [user?.cityId])

  // Get page from URL, default to 1 if not present
  const page = parseInt(searchParams.get('page') || '1', 10)
  const limit = 5

  // Fetch data
  const { data: professionals, isLoading, error } = useProfessionalsByProfession(
    professionId || '',
    {
      cityId: cityId || undefined,
      gender: gender === 'male' || gender === 'female' ? gender : undefined,
      minRating: minRating !== 'all' ? parseInt(minRating) : undefined,
      sortBy,
      page,
      limit,
    }
  )
  const { data: professions } = useProfessions()

  // Get current profession name
  const currentProfession = professions?.find((p) => p._id === professionId)

  // Derive values
  const pagination = professionals?.pagination
  const hasFiltersApplied = !!(cityId || minRating !== 'all' || gender !== 'all')

  // Handlers
  const handleProfessionalClick = async (professional: Professional) => {
    await navigate(`/professional/${professional.id}`)
  }

  const handleContactClick = (professional: Professional) => {
    // Open WhatsApp or phone dialer
    const phone = professional.contactPhone.replace(/\D/g, '')
    window.open(`https://wa.me/${phone}`, '_blank')
  }

  const handlePageChange = (newPage: number) => {
    // Update URL search params
    if (newPage === 1) {
      // Remove page param for page 1 (cleaner URLs)
      searchParams.delete('page')
    } else {
      // Set page param for other pages
      searchParams.set('page', newPage.toString())
    }
    // Use replace: true to prevent pagination from creating browser history entries
    // This ensures the back button goes to the previous route, not through pagination pages
    setSearchParams(searchParams, { replace: true })

    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'instant' })
  }

  const handleClearFilters = () => {
    setCityId('')
    setGender('all')
    setMinRating('all')

    // Reset page to 1 by removing URL param
    searchParams.delete('page')
    setSearchParams(searchParams)
  }

  // ============================================================================
  // Main Content
  // ============================================================================

  return (
    <div className="container mx-auto px-4 py-8">
      <ProfessionalsListHeader
        profession={currentProfession}
      />

      <ProfessionalsSortingFilter
        cityId={cityId}
        gender={gender}
        minRating={minRating}
        sortBy={sortBy}
        onCityChange={setCityId}
        onGenderChange={setGender}
        onRatingChange={setMinRating}
        onSortChange={setSortBy}
        resultCount={
          pagination
            ? {
              from: ((page - 1) * limit) + 1,
              to: Math.min(page * limit, pagination.total),
              total: pagination.total
            }
            : undefined
        }
        isLoading={isLoading}
      />

      <ProfessionalsGrid
        professionals={professionals?.data}
        isLoading={isLoading}
        error={error}
        onProfessionalClick={(p) => void handleProfessionalClick(p)}
        onContactClick={handleContactClick}
        hasFiltersApplied={hasFiltersApplied}
        onClearFilters={handleClearFilters}
      />

      <ProfessionalsPagination
        currentPage={page}
        totalPages={pagination?.totalPages || 1}
        onPageChange={handlePageChange}
      />
    </div>
  )
}
